// src/components/sucursales/Sucursales.js
import React, { useState, useEffect, useMemo } from 'react';
import { useMe } from '../../hooks/useMe';
import { listSucursales, createSucursal, updateSucursal, deleteSucursal, subirLogoSucursal } from '../../services/data';
import Swal from 'sweetalert2';
import '../finanzas/ui.css';
import './Sucursales.css';
import SucursalLogo from './SucursalLogo';
import LogoUploader from './LogoUploader';
import { serviciosDe } from '../../utils/servicios';
import { notificarSucursalesCambiadas } from '../../hooks/useSucursales';
import { useSucursalActiva } from '../../context/SucursalActivaContext';

/* ================= Helpers de rol y JWT ================ */
function decodeJwtPayloadSafe(token) {
  try {
    const base64Url = (token || '').split('.')[1] || '';
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}
const lower = (v) => (v ?? '').toString().trim().toLowerCase();

// Porcentaje de comisión: 0 a 100, hasta 2 decimales (mientras se escribe).
const pctOk = (v) => /^\d{0,3}(?:\.\d{0,2})?$/.test(v) && (v === '' || Number(v) <= 100);
const pctTxt = (v) => `${Number(v ?? 0).toLocaleString('es-GT', { maximumFractionDigits: 2 })}%`;

function getRoleFromStorageOrJWT() {
  const lsRole = lower(localStorage.getItem('role') || '');
  if (lsRole) return lsRole;
  const token = localStorage.getItem('token') || '';
  const p = decodeJwtPayloadSafe(token) || {};
  const jwtRole = lower(p.role || p.rol);
  if (jwtRole) return jwtRole;
  if (p.admin === true || p.isAdmin === true) return 'admin';
  return null;
}

/* ================= Componente ================= */
export default function Sucursales() {
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(false);

  // Perfil del usuario logueado (desde Firestore)
  const me = useMe();
  // Con una sucursal actual solo se ve y edita esa; crear sucursales es desde "Todas las sucursales".
  const { sucursalId: sucActual, todas: vistaGlobal } = useSucursalActiva();

  // Fallback de rol (desde storage/JWT) para no bloquear ediciones de admin
  const [fallbackRole] = useState(getRoleFromStorageOrJWT());

  // Modal + formulario
  const [modalOpen, setModalOpen] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [nombre, setNombre] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null); // imagen nueva elegida, se sube al guardar
  const [guardando, setGuardando] = useState(false);
  const [activo, setActivo] = useState(true);

  // Extras: servicios de cobro de la sucursal (habilitan sus ventas y comisiones)
  const [tienePos, setTienePos] = useState(true);
  const [tienePedidosYa, setTienePedidosYa] = useState(true);
  // Esquema de turnos: 'turnos' (Día y Noche) o 'jornada_unica' (Día completo: una apertura y cierre al día)
  const [modalidadCaja, setModalidadCaja] = useState('turnos');

  // Configuración financiera (por sucursal)
  // Comisiones: el 0 es solo placeholder; se deben indicar (0 si no aplica).
  const [comTarjeta, setComTarjeta] = useState('');
  const [comPedidosYa, setComPedidosYa] = useState('');


  const effectiveRole = me.role || fallbackRole || 'viewer';
  const canManage = effectiveRole === 'admin';

  /* ======== Cargar sucursales según rol ======== */
  const cargarSucursales = async () => {
    try {
      setLoading(true);
      const todas = await listSucursales();
      if (effectiveRole === 'admin') {
        setSucursales(todas);
      } else {
        // Viewer: solo ve su sucursal asignada
        setSucursales(me.sucursalId ? todas.filter((s0) => s0.id === me.sucursalId) : []);
      }
    } catch (err) {
      // Manejar permission-denied elegantemente
      const code = err?.status;
      if (code === 403) {
        Swal.fire('Permiso denegado', 'No tienes permisos para listar sucursales.', 'info');
      } else {
        console.error(err);
        Swal.fire('Error', 'No se pudieron cargar las sucursales', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Espera a conocer el perfil primero
    if (!me.loaded) return;
    cargarSucursales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.loaded, effectiveRole, me.sucursalId]);

  /* ======== Derivados para render ======== */
  const visibleSucursales = useMemo(() => (
    effectiveRole === 'admin' && !vistaGlobal ? sucursales.filter((s0) => s0.id === sucActual) : sucursales
  ), [sucursales, effectiveRole, vistaGlobal, sucActual]);

  /* ======== Acciones ======== */
  const openNewModal = () => {
    if (!canManage) return Swal.fire('Sin permisos', 'No puedes crear sucursales.', 'info');
    setEditandoId(null);
    setNombre('');
    setEmpresa('');
    setUbicacion('');
    setLogoUrl('');
    setLogoFile(null);
    setActivo(true);
    setTienePos(true);
    setTienePedidosYa(true);
    setModalidadCaja('turnos');
    setComTarjeta('');
    setComPedidosYa('');
    setModalOpen(true);
  };

  const openEditModal = (s) => {
    if (!canManage) return Swal.fire('Sin permisos', 'No puedes editar sucursales.', 'info');
    setEditandoId(s.id);
    setNombre(s.nombre || '');
    setEmpresa(s.empresa || '');
    setUbicacion(s.ubicacion || '');
    setLogoUrl(s.logoUrl || '');
    setLogoFile(null);
    setActivo(s.activo !== false);
    const sv = serviciosDe(s);
    setTienePos(sv.pos);
    setTienePedidosYa(sv.pedidosYa);
    setModalidadCaja(s.modalidadCaja || 'turnos');
    setComTarjeta(String(s.comisionTarjeta ?? 0));
    setComPedidosYa(String(s.comisionPedidosYa ?? 0));
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const validar = () => {
    if (!nombre || !empresa || !ubicacion) {
      Swal.fire('Campos incompletos', 'Todos los campos son requeridos', 'warning');
      return false;
    }
    const malo = (v) => v === '' || v.endsWith('.');
    if ((tienePos && malo(comTarjeta)) || (tienePedidosYa && malo(comPedidosYa))) {
      Swal.fire('Comisiones', 'Ingresa la comisión de los servicios activos (0 si no aplica).', 'warning');
      return false;
    }
    return true;
  };

  const handleGuardar = async () => {
    if (!canManage) return Swal.fire('Sin permisos', 'No puedes guardar cambios.', 'info');
    if (!validar()) return;

    setGuardando(true);
    try {
      // Si se eligió una imagen nueva, se sube primero y su URL queda como logo_url.
      const logoFinal = logoFile ? await subirLogoSucursal(logoFile) : logoUrl;
      const payload = {
        nombre: nombre.trim(),
        empresa: empresa.trim(),
        ubicacion: ubicacion.trim(),
        logoUrl: logoFinal,
        activo,
        // Sin el servicio no aplica comisión.
        comisionTarjeta: tienePos ? comTarjeta : '0',
        comisionPedidosYa: tienePedidosYa ? comPedidosYa : '0',
        modalidadCaja,
        extras: {
          ...(editandoId ? sucursales.find((item) => item.id === editandoId)?.extras : {}),
          servicios: { pos: tienePos, pedidosYa: tienePedidosYa },
        },
      };

      if (editandoId) {
        await updateSucursal(editandoId, payload);
        Swal.fire('Actualizado', 'Sucursal modificada', 'success');
      } else {
        await createSucursal(payload);
        Swal.fire('Agregado', 'Sucursal registrada', 'success');
      }

      setModalOpen(false);
      await cargarSucursales();
      notificarSucursalesCambiadas();
    } catch (err) {
      if (err?.status === 403) {
        Swal.fire('Permiso denegado', 'No tienes permisos para editar sucursales.', 'info');
      } else if (err?.status === 409) {
        Swal.fire('No se puede guardar', err.message, 'info');
      } else {
        console.error(err);
        Swal.fire('Error', err?.message || 'No se pudo guardar la sucursal', 'error');
      }
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!canManage) return Swal.fire('Sin permisos', 'No puedes eliminar sucursales.', 'info');
    const confirm = await Swal.fire({
      title: '¿Eliminar?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteSucursal(id);
      await cargarSucursales();
      notificarSucursalesCambiadas();
    } catch (err) {
      if (err?.status === 403) {
        Swal.fire('Permiso denegado', 'No tienes permisos para eliminar sucursales.', 'info');
      } else if (err?.status === 409) {
        Swal.fire('No se puede eliminar', err.message, 'info');
      } else {
        console.error(err);
        Swal.fire('Error', 'No se pudo eliminar', 'error');
      }
    }
  };

  /* ================= Render ================= */
  return (
    <div className="sucursales-shell">
      <header className="sucursales-header">
        <h1>Sucursales</h1>
        <div className="sucursales-actions">
          {canManage && vistaGlobal && (
            <button className="btn btn-primary" onClick={openNewModal}>
              Nueva sucursal
            </button>
          )}
        </div>
      </header>

      {!me.loaded ? (
        <div className="tabla-wrap">
          <table className="tabla">
            <thead>
              <tr>
                <th>Imagen</th>
                <th>Nombre</th>
                <th>Empresa</th>
                <th>Ubicación</th>
                {canManage && <th>Comisiones</th>}
                {canManage && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={canManage ? 6 : 4} className="empty">
                  Cargando perfil…
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="tabla-wrap">
          <table className="tabla">
            <thead>
              <tr>
                <th>Imagen</th>
                <th>Nombre</th>
                <th>Empresa</th>
                <th>Ubicación</th>
                {canManage && <th>Comisiones</th>}
                {canManage && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={canManage ? 6 : 4} className="empty">
                    Cargando…
                  </td>
                </tr>
              ) : visibleSucursales.length ? (
                visibleSucursales.map((s) => (
                  <tr key={s.id}>
                    <td><SucursalLogo sucursal={s} size={68} /></td>
                    <td>{s.nombre}{s.activo === false && <small className="sucursal-inactiva"> (inactiva)</small>}</td>
                    <td>{s.empresa}</td>
                    <td>{s.ubicacion}</td>
                    {canManage && (
                      <td className="sucursal-comisiones">
                        {serviciosDe(s).pos && <span>Tarjeta {pctTxt(s.comisionTarjeta)}</span>}
                        {serviciosDe(s).pedidosYa && <span>PedidosYa {pctTxt(s.comisionPedidosYa)}</span>}
                        {!serviciosDe(s).pos && !serviciosDe(s).pedidosYa && <span>Sin POS ni PedidosYa</span>}
                      </td>
                    )}
                    {canManage && (
                      <td>
                        <div className="acciones">
                          <button className="btn-min" onClick={() => openEditModal(s)}>
                            Editar
                          </button>
                          <button className="btn-min danger" onClick={() => handleEliminar(s.id)}>
                            Eliminar
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canManage ? 6 : 4} className="empty">
                    {effectiveRole === 'viewer'
                      ? (me.sucursalId
                          ? 'No hay datos de tu sucursal.'
                          : 'No tienes una sucursal asignada.')
                      : 'Sin sucursales'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && canManage && (
        <div className="modal-mask" onClick={closeModal}>
          <div className="modal-card fx-compacto" onClick={(e) => e.stopPropagation()}>
            <h3 className="card-title">
              {editandoId ? 'Editar sucursal' : 'Nueva sucursal'}
            </h3>
            <p className="fx-intro">Completa la información. Los campos marcados con * son obligatorios.</p>

            <div className="fx-form form-sucursal">
              <label>Nombre <span className="fx-req" aria-hidden="true">*</span>
                <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre de la sucursal" /></label>
              <label>Empresa <span className="fx-req" aria-hidden="true">*</span>
                <input type="text" value={empresa} onChange={(e) => setEmpresa(e.target.value)} placeholder="Empresa" /></label>
              <label className="full">Ubicación <span className="fx-req" aria-hidden="true">*</span>
                <input type="text" value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} placeholder="Dirección o zona" /></label>
              <div className="fx-campo full">
                <label>Logo de la sucursal</label>
                <LogoUploader nombre={nombre} value={{ url: logoUrl, file: logoFile }}
                  onChange={({ url, file }) => { setLogoUrl(url); setLogoFile(file); }} />
              </div>
              <div className="fx-campo full">
                <label>Servicios</label>
                <div className="sucursal-servicios">
                  <label className="fx-check"><input type="checkbox" checked={tienePos} onChange={(e) => setTienePos(e.target.checked)} /> POS (ventas con tarjeta)</label>
                  <label className="fx-check"><input type="checkbox" checked={tienePedidosYa} onChange={(e) => setTienePedidosYa(e.target.checked)} /> PedidosYa</label>
                </div>
                <span className="fx-help">Solo los servicios activos aparecen en el cierre del cajero y tienen comisión.</span>
              </div>
              <label className="full">Turnos de la sucursal
                <select value={modalidadCaja} onChange={(e) => setModalidadCaja(e.target.value)}>
                  <option value="jornada_unica">Día completo</option>
                  <option value="turnos">Día y Noche</option>
                </select>
                <span className="fx-help">
                  {modalidadCaja === 'turnos'
                    ? 'Dos turnos: el cajero elige Día o Noche al abrir caja.'
                    : 'Una sola caja para todo el día (Día completo).'}
                  {' '}No se cambia con un turno abierto.
                </span>
              </label>
              {tienePos && <label>Comisión Tarjeta
                <span className="pct-input">
                  <input inputMode="decimal" value={comTarjeta} placeholder="0"
                    onChange={(e) => pctOk(e.target.value) && setComTarjeta(e.target.value)}
                    aria-label="Comisión de tarjeta (%)" />
                  <b>%</b>
                </span>
              </label>}
              {tienePedidosYa && <label>Comisión PedidosYa
                <span className="pct-input">
                  <input inputMode="decimal" value={comPedidosYa} placeholder="0"
                    onChange={(e) => pctOk(e.target.value) && setComPedidosYa(e.target.value)}
                    aria-label="Comisión de PedidosYa (%)" />
                  <b>%</b>
                </span>
              </label>}
              {(tienePos || tienePedidosYa) && <span className="fx-help full">Se descuenta del bruto para calcular el neto. Los cierres ya liquidados conservan su comisión.</span>}
              <label className="full fx-check"><input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} /> Sucursal activa</label>
            </div>

            <div className="form-actions">
              <button className="btn" onClick={closeModal}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleGuardar} disabled={guardando}>
                {guardando ? 'Guardando…' : editandoId ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
